import { FC, MouseEvent } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import moment from 'moment';
import { useNavigate } from 'react-router';
import {
  NotificationRo,
  useNotificationControllerSeenNotificationMutation,
} from 'src/app/services/api.generated';
import { useAppDispatch } from 'src/app/reduxCustomHooks';
import { setSelectedNotificationAction } from 'src/app/slices/notificationSlice';
import {
  resolveNotificationHref,
  resolveNotificationNavigateOptions,
} from 'src/utils/notificationNavigation';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

type NotificationListRowProps = {
  notification: NotificationRo;
  unseen?: boolean;
  /** Compact layout for the bell popover. */
  compact?: boolean;
  onNavigated?: () => void;
  showDismiss?: boolean;
};

export const NotificationListRow: FC<NotificationListRowProps> = ({
  notification,
  unseen = false,
  compact = false,
  onNavigated,
  showDismiss = false,
}) => {
  const { id, from, title, text, date, link, meta } = notification;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [seenNotif, { isLoading }] =
    useNotificationControllerSeenNotificationMutation();

  const markSeen = () => {
    if (!unseen) return;
    void seenNotif({ idsDto: { ids: [id] } });
  };

  const openNotification = () => {
    markSeen();
    const href = resolveNotificationHref({ link, meta });
    if (href) {
      navigate(href, resolveNotificationNavigateOptions({ link, meta }));
      onNavigated?.();
      return;
    }
    dispatch(setSelectedNotificationAction(id));
    navigate('/notifications');
    onNavigated?.();
  };

  const dismiss = (event: MouseEvent) => {
    event.stopPropagation();
    markSeen();
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={openNotification}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openNotification();
        }
      }}
      sx={{
        position: 'relative',
        display: 'flex',
        gap: compact ? 1.25 : 1.75,
        px: compact ? 1.5 : 2,
        py: compact ? 1.25 : 1.75,
        cursor: 'pointer',
        borderLeft: '3px solid',
        borderColor: unseen ? 'primary.main' : 'transparent',
        bgcolor: unseen ? 'tint.unseen' : 'transparent',
        transition: `background-color 180ms ${EASE_OUT}, border-color 180ms ${EASE_OUT}`,
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            bgcolor: 'customGrey.dark',
            '& .notif-dismiss': { opacity: 1 },
          },
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
    >
      {showDismiss && (
        <IconButton
          className="notif-dismiss"
          size="small"
          disabled={isLoading}
          aria-label="Dismiss"
          onClick={dismiss}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            opacity: 0,
            transition: `opacity 160ms ${EASE_OUT}`,
            '@media (prefers-reduced-motion: reduce)': {
              opacity: 0.7,
              transition: 'none',
            },
          }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      <Stack
        spacing={0.5}
        sx={{
          minWidth: 0,
          flex: 1,
          pr: showDismiss ? 3 : 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          gap={1}
        >
          <Typography
            fontSize={compact ? 13 : 14}
            fontWeight={unseen ? 600 : 500}
            noWrap
            sx={{ minWidth: 0 }}
          >
            {title}
          </Typography>
          <Tooltip title={moment(date).format('LLLL')}>
            <Typography
              component="span"
              fontSize={compact ? 10 : 11}
              sx={{ opacity: 0.55, flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {moment(date).fromNow()}
            </Typography>
          </Tooltip>
        </Stack>
        <Typography fontSize={compact ? 11 : 12} sx={{ opacity: 0.55 }} noWrap>
          {from}
        </Typography>
        <Typography
          fontSize={compact ? 12 : 13}
          fontWeight={300}
          sx={{
            opacity: 0.85,
            display: '-webkit-box',
            WebkitLineClamp: compact ? 2 : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {text}
        </Typography>
      </Stack>
    </Box>
  );
};
